import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, logout as apiLogout, shopifyAutoLogin } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      setUser(response.data);
    } catch (error) {
      // For Shopify embedded apps, auto-create a session on the backend
      // This ensures the session cookie is properly set for subsequent API calls
      try {
        const autoLoginResponse = await shopifyAutoLogin();
        setUser(autoLoginResponse.data);
      } catch (autoLoginError) {
        console.error('Auto-login failed:', autoLoginError);
        // Fallback to local user for display purposes only
        const shopifyUser = {
          user_id: 'shopify_merchant',
          email: 'merchant@store.com',
          name: 'Store Owner',
          shopify_authenticated: true
        };
        setUser(shopifyUser);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
